import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCalendarMonth,
  MdNumbers,
  MdOutlineLocalHospital,
  MdOutlineQrCode,
  MdOutlineSouthAmerica,
  MdOutlineWorkHistory,
  MdPercent,
  MdWorkOutline,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_EXERCISE_FRAME,
  DATE_FORMAT,
  JOB_LIST,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const CSO_CATEGORIES = [
  SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
  SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
  SOCIO_PROFESSIONAL_CATEGORIES.EMPLOYEES_AND_SUPERVISORS,
  SOCIO_PROFESSIONAL_CATEGORIES.WORKERS,
  SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
  SOCIO_PROFESSIONAL_CATEGORIES.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY,
  SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
  SOCIO_PROFESSIONAL_CATEGORIES.STUDENT,
  SOCIO_PROFESSIONAL_CATEGORIES.OTHER,
];

export const showExerciseFrame = (value) =>
  [
    SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
    SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
    SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
  ].includes(value);

const Activity = ({
  setPercent,
  disabled,
  setDisabled,
  isFieldRequired,
  initialValues,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const [showSiret, setShowSiret] = useState(
    get(initialValues, "project.contact.exerciseFrame") ===
      CONTACT_EXERCISE_FRAME.INDEPENDENT,
  );

  const socioProfessionalCategoryPath =
    "project.contact.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onChange: (value) => {
        if (
          (
            get(
              initialValues,
              "insurancePlan.config.additionalDocuments",
              [],
            ) || []
          ).includes("proof.pensionFund")
        ) {
          setDisabled((prev) => ({
            ...prev,
            [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]:
              value === SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
          }));
        }
        if (!showExerciseFrame(value)) {
          set(
            initialValues,
            "project.contact.exerciseFrame",
            CONTACT_EXERCISE_FRAME.EMPLOYEE,
          );
        }
        setDisabled((prev) => ({
          ...prev,
          project: {
            ...prev.project,
            contact: {
              ...prev.project.contact,
              exerciseFrame: !showExerciseFrame(value),
            },
          },
        }));
      },
      onBlur: () => setPercent(initialValues),
    },
  );

  const exerciseFramePath = "project.contact.exerciseFrame";
  const exerciseFrameField = bindField(exerciseFramePath, {
    label: SUBSCRIPTION.FORM.EXERCISE_FRAMEWORK.LABEL,
    required: isFieldRequired(exerciseFramePath),
    onChange: (value) => {
      setShowSiret(value === CONTACT_EXERCISE_FRAME.INDEPENDENT);
      if (value === CONTACT_EXERCISE_FRAME.EMPLOYEE) {
        set(initialValues, "project.contact.siretNumber", null);
      }
    },
    onBlur: () => setPercent(initialValues),
  });

  const siretNumberPath = "project.contact.siretNumber";
  const siretNumberField = bindField(siretNumberPath, {
    label: SUBSCRIPTION.FORM.SIRET.LABEL,
    required: isFieldRequired(siretNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const professionPath = "project.contact.profession";
  const professionField = bindField(professionPath, {
    label: SUBSCRIPTION.FORM.PROFESSION.LABEL,
    required: isFieldRequired(professionPath),
    disabled: get(disabled, professionPath),
    onBlur: () => setPercent(initialValues),
  });

  const entryDatePath = "project.contact.fields.entryDate";
  const entryDateField = bindField(entryDatePath, {
    label: SUBSCRIPTION.FORM.ENTRY_DATE.LABEL,
    required: isFieldRequired(entryDatePath),
    onBlur: () => setPercent(initialValues),
  });

  const optMadelinPath = "project.contact.optMadelin";
  const optMadelinField = bindField(optMadelinPath, {
    label: SUBSCRIPTION.FORM.MADELIN_LAW.LABEL,
    required: isFieldRequired(optMadelinPath),
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={socioProfessionalCategoryField.path}
              label={socioProfessionalCategoryField.label}
              required={socioProfessionalCategoryField.required}
              disabled={socioProfessionalCategoryField.disabled}
            >
              {(field) => (
                <Select
                  options={CSO_CATEGORIES.map((key) => ({
                    value: key,
                    label:
                      SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY
                        .SOLYON_OPTIONS[key],
                  }))}
                  label={socioProfessionalCategoryField.label}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={
                    SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.PLACEHOLDER
                  }
                  defaultValue={socioProfessionalCategoryField.defaultValue}
                  {...field}
                  onChange={socioProfessionalCategoryField.onChange}
                  onBlur={socioProfessionalCategoryField.onBlur}
                  optional={
                    !socioProfessionalCategoryField.required &&
                    SUBSCRIPTION.FORM.OPTIONAL
                  }
                />
              )}
            </FormField>
          </Col>
          {showExerciseFrame(
            get(initialValues, socioProfessionalCategoryPath),
          ) && (
            <Col lg={24} xs={24}>
              <FormField
                path={exerciseFrameField.path}
                label={exerciseFrameField.label}
                required={exerciseFrameField.required}
                disabled={exerciseFrameField.disabled}
              >
                {(field) => (
                  <Input
                    type="radio"
                    variant="group"
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    icon={<MdOutlineWorkHistory size={16} />}
                    label={exerciseFrameField.label}
                    values={[
                      {
                        value: CONTACT_EXERCISE_FRAME.EMPLOYEE,
                        label:
                          SUBSCRIPTION.FORM.EXERCISE_FRAMEWORK.OPTIONS.EMPLOYEE,
                      },
                      {
                        value: CONTACT_EXERCISE_FRAME.INDEPENDENT,
                        label:
                          SUBSCRIPTION.FORM.EXERCISE_FRAMEWORK.OPTIONS
                            .INDEPENDENT,
                      },
                    ]}
                    value={get(initialValues, exerciseFramePath)}
                    {...field}
                    onChange={exerciseFrameField.onChange}
                    onBlur={exerciseFrameField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}
          {showSiret && (
            <Col lg={24} xs={24}>
              <FormField
                path={siretNumberField.path}
                label={siretNumberField.label}
                required={siretNumberField.required}
                disabled={siretNumberField.disabled}
              >
                {(field) => (
                  <Input
                    label={siretNumberField.label}
                    optional={SUBSCRIPTION.FORM.OPTIONAL}
                    icon={<MdOutlineQrCode size={16} />}
                    placeholder={SUBSCRIPTION.FORM.SIRET.PLACEHOLDER}
                    type="custom"
                    trim={true}
                    defaultValue={siretNumberField.defaultValue}
                    {...field}
                    onChange={siretNumberField.onChange}
                    onBlur={siretNumberField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}
          <Col lg={24} xs={24}>
            <FormField
              path={professionField.path}
              label={professionField.label}
              required={professionField.required}
              disabled={professionField.disabled}
            >
              {(field) => (
                <Select
                  options={Object.entries(JOB_LIST).map(([key, value]) => ({
                    label: SUBSCRIPTION.FORM.PROFESSION.LIST[key],
                    value,
                  }))}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
                  label={professionField.label}
                  defaultValue={professionField.defaultValue}
                  {...field}
                  onChange={professionField.onChange}
                  onBlur={professionField.onBlur}
                  optional={!professionField.required}
                />
              )}
            </FormField>
          </Col>
          {[
            JOB_LIST.HOSPITAL_CIVIL_SERVICE,
            JOB_LIST.HOSPITAL_PRACTITIONER,
          ].includes(get(initialValues, professionPath)) ? (
            <>
              {get(initialValues, professionPath) ===
                JOB_LIST.HOSPITAL_CIVIL_SERVICE && (
                <>
                  <Col lg={24} xs={24}>
                    <Input
                      type="number"
                      icon={<MdNumbers size={16} />}
                      label={SUBSCRIPTION.FORM.CAPPED_INDEX.LABEL}
                      defaultValue={get(
                        initialValues,
                        "project.contact.fields.cappedIndex",
                      )}
                      disabled={true}
                    />
                  </Col>
                  <Col lg={24} xs={24}>
                    <Select
                      icon={<MdPercent size={16} />}
                      label={SUBSCRIPTION.FORM.ACTIVITY_TIME.LABEL}
                      defaultValue={get(
                        initialValues,
                        "project.contact.fields.activityTime",
                      )}
                      disabled={true}
                    />
                  </Col>
                </>
              )}
              <Col lg={24} xs={24}>
                <Select
                  icon={<MdOutlineLocalHospital size={16} />}
                  label={SUBSCRIPTION.FORM.ESTABLISHMENT.LABEL}
                  defaultValue={get(
                    initialValues,
                    "project.contact.fields.establishment",
                  )}
                  disabled={true}
                />
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={entryDateField.path}
                  label={entryDateField.label}
                  required={entryDateField.required}
                  disabled={entryDateField.disabled}
                >
                  {(field) => (
                    <Input
                      type="date"
                      variant="simple"
                      optional
                      label={entryDateField.label}
                      icon={<MdCalendarMonth size={16} />}
                      placeholder={SUBSCRIPTION.FORM.ENTRY_DATE.PLACEHOLDER}
                      defaultValue={
                        get(initialValues, entryDatePath) &&
                        dayjs(get(initialValues, entryDatePath))
                      }
                      format={DATE_FORMAT}
                      disabledDate={(current) =>
                        current >= dayjs().startOf("day")
                      }
                      {...field}
                      onChange={entryDateField.onChange}
                      onBlur={entryDateField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          ) : (
            <Col lg={24} xs={24}>
              <Input
                type="number"
                icon={<MdNumbers size={16} />}
                label={SUBSCRIPTION.FORM.ANNUAL_INCOME.LABEL}
                defaultValue={get(
                  initialValues,
                  "project.contact.fields.income",
                )}
                disabled={true}
              />
            </Col>
          )}
          {!get(disabled, optMadelinPath) && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={optMadelinField.path}
                  label={optMadelinField.label}
                  required={optMadelinField.required}
                  disabled={optMadelinField.disabled}
                >
                  {(field) => (
                    <Input
                      type="radio"
                      variant="group"
                      optional={SUBSCRIPTION.FORM.OPTIONAL}
                      icon={<MdOutlineSouthAmerica size={16} />}
                      label={optMadelinField.label}
                      values={[
                        {
                          value: true,
                          label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.YES,
                        },
                        {
                          value: false,
                          label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.NO,
                        },
                      ]}
                      value={get(initialValues, optMadelinPath)}
                      {...field}
                      onChange={optMadelinField.onChange}
                      onBlur={optMadelinField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <Alert
                  showIcon
                  type="info"
                  message={SUBSCRIPTION.FORM.MADELIN_LAW.INFO}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
